<script setup lang="ts">
import type { CondoPropertiesTypology } from '~/core/entities/condo/Condo';
import type { SectionName } from '~/types/mixpanel';

import { grammarInflection } from '~~/utils';

import { RangeLabel } from '~/components/CharacteristicsGrid';
import { Card } from '~/components/ui/card';

type CondoTypologyCardProps = {
	propertyTypology: CondoPropertiesTypology;
	sectionName: SectionName;
};

const props = defineProps<CondoTypologyCardProps>();

const roomsNumberArray = computed(() => {
	return (
		props.propertyTypology?.rooms
			?.split('e')
			.map(item => Number(item.trim()))
			.filter(item => item !== 0) || []
	);
});

const suitesNumberArray = computed(() => {
	return (
		props.propertyTypology?.suites
			?.split('e')
			.map(item => Number(item.trim()))
			.filter(item => item !== 0) || []
	);
});

const hasRooms = computed(() => {
	return roomsNumberArray.value.length > 0;
});

const hasSuites = computed(() => {
	return suitesNumberArray.value.length > 0;
});

const formattedPrice = computed(() => {
	return formatRange(
		props.propertyTypology?.statistics?.price,
		getFormattedCurrency
	);
});

const formattedRooms = computed(() => {
	return formatNumberArrayWithGrammarInflection(
		roomsNumberArray.value,
		'quarto',
		'quartos'
	);
});

const formattedSuites = computed(() => {
	return formatNumberArrayWithGrammarInflection(
		suitesNumberArray.value,
		'suíte',
		'suítes'
	);
});

const formattedSquareMeterPriceRange = computed(() => {
	return formatRange(
		props.propertyTypology?.statistics?.squareMeterPrice,
		getFormattedSquareMeterPrice
	);
});

const unitsLabel = computed(() => {
	const label = grammarInflection(
		props.propertyTypology?.count || 0,
		'unidade',
		'unidades'
	);

	return `${props.propertyTypology?.count} ${label}`;
});

const hasAreaStart = computed(() => {
	return !!props.propertyTypology?.statistics?.area?.min;
});

const hasSquareMeterPriceStart = computed(() => {
	return !!props.propertyTypology?.statistics?.squareMeterPrice?.min;
});

const hasMoreThanOneProperty = computed(
	() => (Number(props.propertyTypology?.count) || 0) > 1
);

const hasSquareMeterPrice = computed(() => {
	const price = props.propertyTypology?.statistics?.squareMeterPrice;

	return !!price?.min || !!price?.max;
});

const hasPrice = computed(() => {
	return !!formattedPrice.value.min || !!formattedPrice.value.max;
});

function parseArea(value: number): string {
	return `${value} m²`;
}
</script>
<template>
	<Card
		v-if="propertyTypology"
		class="flex h-full shrink-0 grow-0 flex-col divide-y divide-beige rounded-lg bg-beige-light-2"
	>
		<div class="flex size-full flex-col gap-5 px-6 pb-8 pt-6 md:gap-6 md:!p-8">
			<div class="flex items-center gap-2 text-xs font-medium leading-[150%]">
				<Badge variant="beige-default" class="w-fit px-2 py-1">
					<span>{{ propertyTypology.type }}</span>
				</Badge>
				<Badge variant="beige-default" class="w-fit px-2 py-1">
					<span> {{ unitsLabel }} </span>
				</Badge>
			</div>

			<div class="flex items-baseline text-primary">
				<RangeLabel
					v-if="hasMoreThanOneProperty"
					:range-start="formattedPrice.min"
					:range-end="formattedPrice.max"
					size="xl"
					icon-class="size-4"
					value-class="font-matter-sq leading-[150%] tracking-[-0.36px] text-primary"
				/>
				<span
					v-else-if="hasPrice"
					class="font-matter-sq text-lg font-normal leading-[150%] tracking-[-0.36px] text-primary md:text-xl"
				>
					{{ formattedPrice.min ? formattedPrice.min : formattedPrice.max }}
				</span>
				<span
					v-else
					class="font-matter-sq text-lg font-normal leading-[150%] tracking-[-0.36px] md:text-xl"
				>
					Sob consulta
				</span>
			</div>

			<div class="flex flex-col">
				<div class="flex items-end">
					<RangeLabel
						:has-start="hasAreaStart"
						:range-start="
							parseArea(propertyTypology.statistics?.area?.min || 0)
						"
						:range-end="parseArea(propertyTypology.statistics?.area?.max || 0)"
						size="sm"
						icon-class="size-4 md:size-2"
					/>
				</div>
				<div class="flex items-baseline gap-[6px] text-primary-light-3">
					<template v-if="hasSquareMeterPrice">
						<span class="text-sm font-normal">Área útil</span>
						<span
							v-if="hasMoreThanOneProperty && hasSquareMeterPriceStart"
							class="text-xs font-normal"
						>
							({{ formattedSquareMeterPriceRange.min }} a
							{{ formattedSquareMeterPriceRange.max }})
						</span>
						<span v-else class="text-xs font-normal">
							({{
								formattedSquareMeterPriceRange.min
									? formattedSquareMeterPriceRange.min
									: formattedSquareMeterPriceRange.max
							}})
						</span>
					</template>
				</div>
			</div>

			<div
				v-if="hasRooms || hasSuites"
				class="flex items-start gap-2 text-sm font-normal text-primary md:text-base"
			>
				<div v-if="hasRooms" class="flex flex-col">
					<span>
						{{ formattedRooms }}
					</span>
				</div>

				<div
					v-if="hasRooms && hasSuites"
					class="size-1 self-center bg-beige-dark-1"
				/>

				<div v-if="hasSuites" class="flex flex-col">
					<span>
						{{ formattedSuites }}
					</span>
				</div>
			</div>
		</div>
		<div class="flex items-start px-6 py-4 md:px-8">
			<slot name="cta" />
		</div>
	</Card>
</template>
